'use client';

import React, { useState } from 'react';
import { toast } from 'sonner';

const TABS = [
  { id: 'general', label: 'Général', icon: '⚙️' },
  { id: 'notifications', label: 'Notifications', icon: '🔔' },
  { id: 'matching', label: 'Matching', icon: '🔗' },
  { id: 'securite', label: 'Sécurité', icon: '🔒' },
  { id: 'integrations', label: 'Intégrations', icon: '🔌' },
];

interface GeneralSettings {
  nomPortail: string;
  devise: string;
  langue: string;
  fuseau: string;
  formatDate: string;
  logoUrl: string;
}

interface MatchingWeights {
  localisation: number;
  surface: number;
  facade: number;
  budget: number;
  technique: number;
  timing: number;
}

export default function ParametresContent() {
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);

  const [general, setGeneral] = useState<GeneralSettings>({
    nomPortail: 'Axxam Retail Listings Listings Pro',
    devise: 'MAD',
    langue: 'Français',
    fuseau: 'Africa/Casablanca (UTC+1)',
    formatDate: 'JJ/MM/AAAA',
    logoUrl: '',
  });

  const [weights, setWeights] = useState<MatchingWeights>({
    localisation: 25,
    surface: 20,
    facade: 15,
    budget: 20,
    technique: 10,
    timing: 10,
  });

  const [notifs, setNotifs] = useState({
    nouveauLocal: true,
    nouvelleVisite: true,
    matchHautScore: true,
    documentManquant: false,
    rappelVisite: true,
    offreRecue: true,
    emailDigest: false,
  });

  const totalWeight = Object.values(weights).reduce((a, b) => a + b, 0);

  const handleSave = async () => {
    setSaving(true);
    await new Promise(r => setTimeout(r, 800));
    setSaving(false);
    toast.success('Paramètres sauvegardés avec succès !');
  };

  const inputCls = 'input-field';
  const labelCls = 'block text-xs font-600 text-slate-600 mb-1.5 uppercase tracking-wide';

  return (
    <div className="px-6 lg:px-8 xl:px-10 py-7 max-w-screen-xl mx-auto">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-700 text-slate-900">Paramètres</h1>
          <p className="text-sm text-slate-500 mt-0.5">Configuration du portail Axxam Retail Listings Listings Pro</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn-primary">
          {saving ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Enregistrement...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Sauvegarder
            </>
          )}
        </button>
      </div>

      <div className="flex gap-6">
        {/* Tab nav */}
        <div className="hidden lg:flex flex-col gap-1 w-52 flex-shrink-0">
          <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm sticky top-4">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 text-left ${
                  activeTab === tab.id ? 'bg-primary-50 text-primary-700 font-600' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Mobile tabs */}
        <div className="lg:hidden flex gap-2 overflow-x-auto pb-2 mb-4 w-full">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm font-600 transition-all ${activeTab === tab.id ? 'bg-primary-700 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-5">

          {/* General */}
          {activeTab === 'general' && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <h2 className="text-base font-700 text-slate-900 mb-5 flex items-center gap-2">
                <span>⚙️</span> Paramètres généraux
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={labelCls}>Nom du portail</label>
                  <input
                    type="text"
                    className={inputCls}
                    value={general.nomPortail}
                    onChange={e => setGeneral(g => ({ ...g, nomPortail: e.target.value }))}
                  />
                </div>
                <div>
                  <label className={labelCls}>Devise</label>
                  <select className={inputCls} value={general.devise} onChange={e => setGeneral(g => ({ ...g, devise: e.target.value }))}>
                    <option>MAD</option>
                    <option>EUR</option>
                    <option>USD</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Langue</label>
                  <select className={inputCls} value={general.langue} onChange={e => setGeneral(g => ({ ...g, langue: e.target.value }))}>
                    <option>Français</option>
                    <option>Arabe</option>
                    <option>Anglais</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Fuseau horaire</label>
                  <select className={inputCls} value={general.fuseau} onChange={e => setGeneral(g => ({ ...g, fuseau: e.target.value }))}>
                    <option>Africa/Casablanca (UTC+1)</option>
                    <option>Europe/Paris (UTC+2)</option>
                    <option>UTC</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Format de date</label>
                  <select className={inputCls} value={general.formatDate} onChange={e => setGeneral(g => ({ ...g, formatDate: e.target.value }))}>
                    <option>JJ/MM/AAAA</option>
                    <option>MM/JJ/AAAA</option>
                    <option>AAAA-MM-JJ</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-slate-100">
                <h3 className="text-sm font-700 text-slate-800 mb-4">Identité visuelle</h3>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-navy-700 flex items-center justify-center text-white font-700 text-lg flex-shrink-0">
                    LM
                  </div>
                  <div>
                    <p className="text-sm font-600 text-slate-700">Logo du portail</p>
                    <p className="text-xs text-slate-400 mb-2">PNG ou SVG, fond transparent recommandé</p>
                    <button className="btn-secondary text-xs px-3 py-1.5">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Changer le logo
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notifications */}
          {activeTab === 'notifications' && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <h2 className="text-base font-700 text-slate-900 mb-5 flex items-center gap-2">
                <span>🔔</span> Préférences de notifications
              </h2>
              <div className="space-y-4">
                {[
                  { key: 'nouveauLocal', label: 'Nouveau local ajouté', desc: 'Notifier lors de l\'ajout d\'un nouveau local' },
                  { key: 'nouvelleVisite', label: 'Nouvelle visite planifiée', desc: 'Rappel 24h avant chaque visite' },
                  { key: 'matchHautScore', label: 'Match avec score élevé (≥85)', desc: 'Alerte immédiate pour les correspondances prioritaires' },
                  { key: 'documentManquant', label: 'Document manquant', desc: 'Rappel hebdomadaire pour les fiches incomplètes' },
                  { key: 'rappelVisite', label: 'Rappel de visite (J-1)', desc: 'Notification la veille de chaque visite planifiée' },
                  { key: 'offreRecue', label: 'Offre commerciale reçue', desc: 'Notification immédiate lors de la réception d\'une offre' },
                  { key: 'emailDigest', label: 'Résumé hebdomadaire par email', desc: 'Récapitulatif des activités chaque lundi matin' },
                ].map(({ key, label, desc }) => (
                  <div key={key} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                    <div>
                      <p className="text-sm font-600 text-slate-800">{label}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 ml-4">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={notifs[key as keyof typeof notifs]}
                        onChange={e => setNotifs(n => ({ ...n, [key]: e.target.checked }))}
                      />
                      <div className="w-10 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-5 peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-700"></div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Matching weights */}
          {activeTab === 'matching' && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <h2 className="text-base font-700 text-slate-900 mb-2 flex items-center gap-2">
                <span>🔗</span> Pondération du moteur de matching
              </h2>
              <p className="text-xs text-slate-400 mb-5">Ajustez les poids de chaque critère. Le total doit être égal à 100%.</p>

              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg mb-5 text-sm font-600 ${totalWeight === 100 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                Total : {totalWeight}% {totalWeight === 100 ? '✓ Équilibré' : '⚠ Doit être 100%'}
              </div>

              <div className="space-y-5">
                {[
                  { key: 'localisation', label: 'Localisation', desc: 'Correspondance ville, quartier, zone commerciale', color: 'bg-blue-500' },
                  { key: 'surface', label: 'Surface', desc: 'Adéquation surface totale et RDC avec les besoins', color: 'bg-emerald-500' },
                  { key: 'facade', label: 'Façade & Vitrine', desc: 'Linéaire de façade et nombre de vitrines', color: 'bg-violet-500' },
                  { key: 'budget', label: 'Budget', desc: 'Loyer et conditions financières vs budget enseigne', color: 'bg-amber-500' },
                  { key: 'technique', label: 'Technique', desc: 'Hauteur, puissance, extraction, accessibilité', color: 'bg-rose-500' },
                  { key: 'timing', label: 'Timing', desc: 'Disponibilité vs horizon d\'ouverture souhaité', color: 'bg-cyan-500' },
                ].map(({ key, label, desc, color }) => (
                  <div key={key}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div>
                        <span className="text-sm font-600 text-slate-800">{label}</span>
                        <span className="text-xs text-slate-400 ml-2">{desc}</span>
                      </div>
                      <span className="text-sm font-700 text-slate-900 w-10 text-right">{weights[key as keyof MatchingWeights]}%</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={50}
                      step={5}
                      value={weights[key as keyof MatchingWeights]}
                      onChange={e => setWeights(w => ({ ...w, [key]: Number(e.target.value) }))}
                      className="w-full accent-primary-700"
                    />
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                      <div className={`h-full rounded-full ${color}`} style={{ width: `${weights[key as keyof MatchingWeights] * 2}%` }} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 pt-4 border-t border-slate-100">
                <button
                  onClick={() => setWeights({ localisation: 25, surface: 20, facade: 15, budget: 20, technique: 10, timing: 10 })}
                  className="btn-secondary text-xs px-3 py-1.5"
                >
                  Réinitialiser les valeurs par défaut
                </button>
              </div>
            </div>
          )}

          {/* Sécurité */}
          {activeTab === 'securite' && (
            <div className="space-y-5">
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <h2 className="text-base font-700 text-slate-900 mb-5 flex items-center gap-2">
                  <span>🔒</span> Sécurité du compte
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className={labelCls}>Mot de passe actuel</label>
                    <input type="password" className={inputCls} placeholder="••••••••" />
                  </div>
                  <div>
                    <label className={labelCls}>Nouveau mot de passe</label>
                    <input type="password" className={inputCls} placeholder="••••••••" />
                  </div>
                  <div>
                    <label className={labelCls}>Confirmer le nouveau mot de passe</label>
                    <input type="password" className={inputCls} placeholder="••••••••" />
                  </div>
                  <button className="btn-primary text-sm">Changer le mot de passe</button>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <h3 className="text-sm font-700 text-slate-900 mb-4">Authentification à deux facteurs</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-700">Authentification 2FA par SMS</p>
                    <p className="text-xs text-slate-400 mt-0.5">Sécurisez votre compte avec une vérification en deux étapes</p>
                  </div>
                  <span className="status-badge bg-slate-50 text-slate-500 border border-slate-200">Non activé</span>
                </div>
                <button className="btn-secondary text-xs px-3 py-1.5 mt-3">Activer la 2FA</button>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <h3 className="text-sm font-700 text-slate-900 mb-4">Sessions actives</h3>
                <div className="space-y-3">
                  {[
                    { device: 'Chrome — Windows 11', location: 'Casablanca, MA', time: 'Session actuelle', current: true },
                    { device: 'Safari — iPhone 15', location: 'Casablanca, MA', time: 'Il y a 2 heures', current: false },
                    { device: 'Firefox — macOS', location: 'Rabat, MA', time: 'Il y a 3 jours', current: false },
                  ].map((session, i) => (
                    <div key={i} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${session.current ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        <div>
                          <p className="text-sm font-600 text-slate-800">{session.device}</p>
                          <p className="text-xs text-slate-400">{session.location} · {session.time}</p>
                        </div>
                      </div>
                      {!session.current && (
                        <button
                          onClick={() => toast.success('Session révoquée')}
                          className="text-xs text-red-500 hover:text-red-700 font-600 transition-colors"
                        >
                          Révoquer
                        </button>
                      )}
                      {session.current && <span className="text-xs text-emerald-600 font-600">Actuelle</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Intégrations */}
          {activeTab === 'integrations' && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <h2 className="text-base font-700 text-slate-900 mb-5 flex items-center gap-2">
                <span>🔌</span> Intégrations & API
              </h2>
              <div className="space-y-4">
                {[
                  { name: 'Google Maps', desc: 'Géolocalisation et cartographie des locaux', statut: 'Connecté', color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', icon: '🗺️' },
                  { name: 'Supabase', desc: 'Base de données et authentification', statut: 'Connecté', color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', icon: '🗄️' },
                  { name: 'SendGrid', desc: 'Envoi d\'emails transactionnels et notifications', statut: 'Non configuré', color: 'text-slate-500', bg: 'bg-slate-50 border-slate-200', icon: '📧' },
                  { name: 'DocuSign', desc: 'Signature électronique des baux et contrats', statut: 'Non configuré', color: 'text-slate-500', bg: 'bg-slate-50 border-slate-200', icon: '✍️' },
                  { name: 'Calendly', desc: 'Planification automatique des visites', statut: 'Non configuré', color: 'text-slate-500', bg: 'bg-slate-50 border-slate-200', icon: '📅' },
                  { name: 'Zapier', desc: 'Automatisation des workflows métier', statut: 'Non configuré', color: 'text-slate-500', bg: 'bg-slate-50 border-slate-200', icon: '⚡' },
                ].map(integ => (
                  <div key={integ.name} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{integ.icon}</span>
                      <div>
                        <p className="text-sm font-600 text-slate-800">{integ.name}</p>
                        <p className="text-xs text-slate-400">{integ.desc}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`status-badge border text-xs ${integ.bg} ${integ.color}`}>{integ.statut}</span>
                      <button
                        onClick={() => toast.success(`Configuration ${integ.name} ouverte`)}
                        className="btn-secondary text-xs px-3 py-1.5"
                      >
                        {integ.statut === 'Connecté' ? 'Configurer' : 'Connecter'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 pt-4 border-t border-slate-100">
                <p className="text-xs font-700 text-slate-600 uppercase tracking-wide mb-3">Clé API du portail</p>
                <div className="flex items-center gap-3">
                  <input
                    type="password"
                    className={`${inputCls} font-mono text-xs`}
                    value="lm_sk_prod_••••••••••••••••••••••••••••••"
                    readOnly
                  />
                  <button
                    onClick={() => toast.success('Clé API copiée !')}
                    className="btn-secondary text-xs px-3 py-2 flex-shrink-0"
                  >
                    Copier
                  </button>
                  <button
                    onClick={() => toast.error('Régénération de clé API — confirmation requise')}
                    className="btn-secondary text-xs px-3 py-2 flex-shrink-0 text-red-500 hover:text-red-700"
                  >
                    Régénérer
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
